"use client"

import type { Theme } from "@/models/themes.model"
import { useQuery } from "@tanstack/react-query"
import { useTheme as useNextTheme } from "next-themes"
import React, { createContext, use, useCallback, useEffect, useMemo, useState } from "react"
import { useLocalStorage } from "usehooks-ts"
import { useConfigurationsQuery } from "@/hooks/react-query/themes"
import { themeQueryOptions } from "@/queries/theme.factory"
import { setFont } from "@/utils/fonts"
import { sanitizeCssClassName } from "@/utils/theme-utils"

// Context type
type CustomThemeContextType = {
  activeTheme: Theme | undefined
  setActiveTheme: (theme: Theme | undefined, variant?: "light" | "dark") => void
  themeList: Theme[]
  colorMode: string | undefined
}

const LOCAL_STORAGE_KEY = "active_theme"

const CustomThemeContext = createContext<CustomThemeContextType | undefined>(undefined)

export function CustomThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme: colorMode, setTheme } = useNextTheme()
  const [mounted, setMounted] = useState(false)
  const { data: themeList } = useQuery(themeQueryOptions.all)
  const { grsSettings } = useConfigurationsQuery()

  // State for the active theme (object)
  const [activeTheme, setActiveThemeState] = useLocalStorage<Theme | undefined>(LOCAL_STORAGE_KEY, undefined)

  // Apply theme logic (from use-theme.ts)
  const applyTheme = useCallback((selectedTheme: Theme | undefined, variant?: "light" | "dark") => {
    if (!selectedTheme) {
      return
    }
    if (selectedTheme) {
      // Remove any existing classes from the root element
      const root = document.documentElement
      Array.from(root.classList).forEach((className) => {
        if (className.startsWith("theme-")) {
          root.classList.remove(className)
        }
      })
      // Create a new style element for the theme
      const styleId = `theme-${selectedTheme.name}-${variant ?? "light"}`
      let styleElement = document.getElementById(styleId)
      if (styleElement) {
        styleElement.remove()
      }
      styleElement = document.createElement("style")
      styleElement.id = styleId
      // Generate css rules for the theme
      const cssRules = Object.entries(selectedTheme.colors?.[variant ?? "light"] ?? {})
        .map(([key, value]) => {
          if (key === "radius") {
            return `  --${key}: ${value};`
          }
          if (key.startsWith("chart-")) {
            // we ignore the chart colors
            return ""
          }
          if (key === "sidebar-background") {
            return `  --sidebar: oklch(from hsl(${value}) l c h);`
          }
          return `  --${key}: oklch(from hsl(${value}) l c h);`
        })
        .join("\n")
      // Create the css class
      const className = `theme-${sanitizeCssClassName(selectedTheme.name)}-${variant ?? "light"}`
      styleElement.textContent = `.${className} {\n${cssRules}\n}`
      // Add the style element to the document
      document.head.appendChild(styleElement)
      // Apply the theme class to the root element
      root.classList.add(className)
      // Update next-themes color mode
      if (variant === "dark") {
        root.style.setProperty("color-scheme", "dark")
        setTheme("dark")
      }
      else {
        root.style.setProperty("color-scheme", "light")
        setTheme("light")
      }
    }
  }, [setTheme])

  const setActiveTheme = useCallback((selectedTheme: Theme | undefined, variant?: "light" | "dark") => {
    if (!selectedTheme) {
      return
    }

    const activeColorMode = variant || (colorMode as "light" | "dark")

    // Check if theme is the same and we have cached colors
    if (selectedTheme.name === activeTheme?.name
      && Object.keys(activeTheme?.colors ?? {}).length > 0) {
      // Check if colors are actually different
      if (JSON.stringify(activeTheme.colors) === JSON.stringify(selectedTheme.colors)) {
        // If colors are the same but variant changed, we still need to apply the theme
        if (variant !== (colorMode as "light" | "dark")) {
          console.log("color mode changed, switching to", variant)
          applyTheme(selectedTheme, variant)
          return
        }
        console.log("theme is the same, no need to apply")
        return
      }
      else {
        console.log("theme colors changed, applying new colors")
        setActiveThemeState(selectedTheme)
      }
    }

    // Only apply if theme is actually different
    if (selectedTheme !== activeTheme) {
      setActiveThemeState(selectedTheme)
    }

    applyTheme(selectedTheme, activeColorMode)
  }, [activeTheme, colorMode, applyTheme, setActiveThemeState])

  const initTheme = useCallback(() => {
    // If we have a cached theme with colors, check if its the same as the grsSetting theme
    if (activeTheme && Object.keys(activeTheme.colors ?? {}).length > 0 && activeTheme.name === grsSettings?.theme_bydefault) {
      console.log("applying cached theme", activeTheme.name)
      applyTheme(activeTheme, colorMode as "light" | "dark")
    }
    // Otherwise, try to find and apply the default theme
    else if (themeList && themeList.length > 0 && grsSettings?.theme_bydefault) {
      const defaultThemeName = grsSettings.theme_bydefault
      const initialTheme = themeList.find(t => t.name === defaultThemeName)
      if (initialTheme) {
        console.log("applying default theme", initialTheme.name)
        setActiveTheme(initialTheme, colorMode as "light" | "dark")
      }
    }

    // Set the font regardless of theme
    void setFont(grsSettings?.font_platform as string ?? "Inter")
  }, [activeTheme, colorMode, themeList, grsSettings, applyTheme, setActiveTheme])

  // When mounted on client, now we can show the UI
  // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    initTheme()
  }, [initTheme])

  // Context value
  const value = useMemo(() => ({
    activeTheme,
    setActiveTheme,
    themeList: themeList ?? [],
    colorMode,
  }), [activeTheme, setActiveTheme, themeList, colorMode])

  if (!mounted) {
    return null
  }

  return (
    <CustomThemeContext value={value}>
      {children}
    </CustomThemeContext>
  )
}

export function useCustomTheme() {
  const context = use(CustomThemeContext)
  if (!context) {
    throw new Error("useCustomTheme must be used within a CustomThemeProvider")
  }
  return context
}
